import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const FooterContainer = styled(Box)({
  background: 'linear-gradient(180deg, #0f0f0f 0%, #0a0a0a 100%)',
  padding: '100px 60px 40px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.3), transparent)'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px 32px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto'
});

const FooterTop = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1.5fr 1fr 1fr 1fr',
  gap: '60px',
  marginBottom: '80px',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '48px'
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
    gap: '40px'
  }
});

const BrandSection = styled(Box)({});

const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '24px'
});

const LogoMark = styled(Box)({
  width: '48px',
  height: '48px',
  background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'Playfair Display', serif",
  fontSize: '24px',
  fontWeight: 700,
  color: '#1a1a2e',
  boxShadow: '0 4px 20px rgba(212, 175, 55, 0.3)'
});

const LogoText = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: '24px',
  fontWeight: 600,
  color: '#ffffff',
  letterSpacing: '-0.5px'
});

const Tagline = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.5)',
  lineHeight: 1.8,
  maxWidth: '320px',
  marginBottom: '32px'
});

const SocialIcons = styled(Box)({
  display: 'flex',
  gap: '12px'
});

const SocialIcon = styled(IconButton)({
  width: '48px',
  height: '48px',
  borderRadius: '14px',
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  color: 'rgba(255, 255, 255, 0.6)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
    borderColor: '#d4af37',
    color: '#1a1a2e',
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 25px rgba(212, 175, 55, 0.3)'
  }
});

const FooterColumn = styled(Box)({});

const ColumnTitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  color: '#d4af37',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginBottom: '28px'
});

const NavLinks = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
});

const NavLink = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.6)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    color: '#d4af37',
    paddingLeft: '8px'
  }
});

const ContactItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    '& svg': {
      color: '#d4af37'
    },
    '& p': {
      color: '#ffffff'
    }
  }
});

const ContactIcon = styled(Box)({
  '& svg': {
    fontSize: '20px',
    color: 'rgba(255, 255, 255, 0.4)',
    transition: 'color 0.3s ease'
  }
});

const ContactText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.6)',
  transition: 'color 0.3s ease'
});

const Divider = styled(Box)({
  height: '1px',
  background: 'rgba(255, 255, 255, 0.08)',
  marginBottom: '32px'
});

const FooterBottom = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    gap: '20px',
    textAlign: 'center'
  }
});

const Copyright = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.4)'
});

const BackToTop = styled(IconButton)({
  position: 'fixed',
  bottom: '36px',
  right: '36px',
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #d4af37 0%, #f4e4bc 100%)',
  color: '#1a1a2e',
  zIndex: 1000,
  boxShadow: '0 8px 30px rgba(212, 175, 55, 0.4)',
  transition: 'all 0.3s ease',
  animation: `${float} 3s ease-in-out infinite`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(212, 175, 55, 0.5)'
  }
});

const navItems = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Contact', href: '#contact' }
];

const Footer = ({ businessInfo, socialMedia, contactInfo }) => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavClick = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const businessName = businessInfo?.name || 'Business Name';
  const firstLetter = businessName.charAt(0).toUpperCase();

  return (
    <FooterContainer>
      <ContentWrapper>
        <FooterTop>
          <BrandSection>
            <LogoContainer>
              <LogoMark>{firstLetter}</LogoMark>
              <LogoText>{businessName}</LogoText>
            </LogoContainer>
            <Tagline>
              {businessInfo?.tagline || 'Delivering excellence and creating lasting impressions through premium services.'}
            </Tagline>
            <SocialIcons>
              {socialMedia?.facebook && (
                <SocialIcon onClick={() => window.open(socialMedia.facebook, '_blank')}>
                  <FacebookIcon />
                </SocialIcon>
              )}
              {socialMedia?.twitter && (
                <SocialIcon onClick={() => window.open(socialMedia.twitter, '_blank')}>
                  <TwitterIcon />
                </SocialIcon>
              )}
              {socialMedia?.instagram && (
                <SocialIcon onClick={() => window.open(socialMedia.instagram, '_blank')}>
                  <InstagramIcon />
                </SocialIcon>
              )}
              {socialMedia?.linkedin && (
                <SocialIcon onClick={() => window.open(socialMedia.linkedin, '_blank')}>
                  <LinkedInIcon />
                </SocialIcon>
              )}
              {!socialMedia && (
                <>
                  <SocialIcon><FacebookIcon /></SocialIcon>
                  <SocialIcon><TwitterIcon /></SocialIcon>
                  <SocialIcon><InstagramIcon /></SocialIcon>
                  <SocialIcon><LinkedInIcon /></SocialIcon>
                </>
              )}
            </SocialIcons>
          </BrandSection>

          <FooterColumn>
            <ColumnTitle>Navigation</ColumnTitle>
            <NavLinks>
              {navItems.slice(0, 3).map((item) => (
                <NavLink key={item.label} onClick={() => handleNavClick(item.href)}>
                  {item.label}
                </NavLink>
              ))}
            </NavLinks>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Explore</ColumnTitle>
            <NavLinks>
              {navItems.slice(3).map((item) => (
                <NavLink key={item.label} onClick={() => handleNavClick(item.href)}>
                  {item.label}
                </NavLink>
              ))}
            </NavLinks>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Contact</ColumnTitle>
            {contactInfo?.email && (
              <ContactItem onClick={() => window.location.href = `mailto:${contactInfo.email}`}>
                <ContactIcon><EmailIcon /></ContactIcon>
                <ContactText>{contactInfo.email}</ContactText>
              </ContactItem>
            )}
            {contactInfo?.phone && (
              <ContactItem onClick={() => window.location.href = `tel:${contactInfo.phone}`}>
                <ContactIcon><PhoneIcon /></ContactIcon>
                <ContactText>{contactInfo.phone}</ContactText>
              </ContactItem>
            )}
          </FooterColumn>
        </FooterTop>

        <Divider />

        <FooterBottom>
          <Copyright>
            {new Date().getFullYear()} {businessName}. All rights reserved.
          </Copyright>
        </FooterBottom>
      </ContentWrapper>

      <BackToTop onClick={scrollToTop}>
        <ArrowUpwardIcon />
      </BackToTop>
    </FooterContainer>
  );
};

export default Footer;
